import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { resolveCollisions, type BuildingCollider } from './CollisionDetection';
import { useGameStore } from '../store/gameStore';
import { joystickInput } from '../ui/VirtualJoystick';
import { terrainHeight } from '../scene/Terrain';

interface CharacterControllerState {
  velocity: THREE.Vector3;
  isMoving: boolean;
  direction: THREE.Vector3;
  position: THREE.Vector3;
  facingAngle: number; // character's current facing yaw
}

export type { CharacterControllerState };

const MOVE_SPEED = 5;
const PLAYER_RADIUS = 0.4;
const ROTATION_SPEED = 5; // radians/sec — how fast A/D turns the character

/**
 * CharacterController — tank-style WASD controls.
 * W = forward (character's facing), S = back.
 * A = turn left, D = turn right (rotate character in place).
 * Camera is always behind (handled by ThirdPersonCamera).
 * Also supports click-to-move via moveTargetRef.
 */
export function useCharacterController(
  buildings: BuildingCollider[],
  spawnPoint: [number, number, number],
  moveTargetRef?: React.MutableRefObject<THREE.Vector3 | null>,
) {
  const stateRef = useRef<CharacterControllerState>({
    velocity: new THREE.Vector3(),
    isMoving: false,
    direction: new THREE.Vector3(0, 0, -1), // face north toward stages
    position: new THREE.Vector3(
      spawnPoint[0],
      terrainHeight(spawnPoint[0], spawnPoint[2]) + 0.65,
      spawnPoint[2],
    ),
    facingAngle: Math.PI, // facing -Z (north)
  });

  const keysRef = useRef<Set<string>>(new Set());
  const setPlayerTransform = useGameStore((s) => s.setPlayerTransform);

  // Keyboard input
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      keysRef.current.add(e.key.toLowerCase());
      if (['w', 'a', 's', 'd', 'arrowup', 'arrowdown', 'arrowleft', 'arrowright'].includes(e.key.toLowerCase())) {
        e.preventDefault();
      }
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      keysRef.current.delete(e.key.toLowerCase());
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // Game loop
  useEffect(() => {
    let raf: number;
    let prevTime = performance.now();

    function tick() {
      const now = performance.now();
      const delta = Math.min((now - prevTime) / 1000, 0.1);
      prevTime = now;

      const keys = keysRef.current;
      const state = stateRef.current;

      // Calculate movement input — tank controls
      let inputZ = 0;        // forward/backward
      let rotationInput = 0; // turn left/right

      if (keys.has('w') || keys.has('arrowup')) inputZ += 1;
      if (keys.has('s') || keys.has('arrowdown')) inputZ -= 1;
      if (keys.has('a') || keys.has('arrowleft')) rotationInput += 1;  // turn left
      if (keys.has('d') || keys.has('arrowright')) rotationInput -= 1; // turn right

      // Mobile joystick input (if no keyboard)
      if (inputZ === 0 && rotationInput === 0) {
        const j = joystickInput.current;
        if (j.x !== 0 || j.z !== 0) {
          rotationInput = -j.x;  // joystick X → turn (inverted: push left = turn left)
          inputZ = j.z;         // joystick Z → forward/backward
        }
      }

      const hasInput = inputZ !== 0 || rotationInput !== 0;

      // Click-to-move: if no keyboard/joystick input, walk toward click target
      if (!hasInput && moveTargetRef?.current) {
        const target = moveTargetRef.current;
        const dx = target.x - state.position.x;
        const dz = target.z - state.position.z;
        const dist = Math.sqrt(dx * dx + dz * dz);

        if (dist < 0.5) {
          // Reached target — stop
          moveTargetRef.current = null;
          state.isMoving = false;
          state.velocity.set(0, 0, 0);
        } else {
          // Walk toward target
          inputZ = 1; // forward relative to facing
          // Turn toward target first, then move
          const targetAngle = Math.atan2(dx, dz);
          let diff = targetAngle - state.facingAngle;
          while (diff > Math.PI) diff -= Math.PI * 2;
          while (diff < -Math.PI) diff += Math.PI * 2;
          state.facingAngle += diff * Math.min(delta * ROTATION_SPEED, 1);

          // Only walk forward once roughly facing target
          if (Math.abs(diff) < 0.3) {
            const worldX = Math.sin(state.facingAngle);
            const worldZ = Math.cos(state.facingAngle);
            state.direction.set(worldX, 0, worldZ).normalize();
            state.velocity.copy(state.direction).multiplyScalar(MOVE_SPEED);
            state.isMoving = true;
          } else {
            state.isMoving = false;
            state.velocity.set(0, 0, 0);
          }
        }
      } else {
        state.isMoving = inputZ !== 0;
      }

      // Apply rotation input directly (A/D — tank turn in place)
      if (rotationInput !== 0) {
        state.facingAngle += rotationInput * delta * ROTATION_SPEED;
        // Normalize
        while (state.facingAngle > Math.PI) state.facingAngle -= Math.PI * 2;
        while (state.facingAngle < -Math.PI) state.facingAngle += Math.PI * 2;
      }

      // Apply movement — always in the character's facing direction
      if (inputZ !== 0) {
        const forwardX = Math.sin(state.facingAngle);
        const forwardZ = Math.cos(state.facingAngle);

        state.direction.set(forwardX, 0, forwardZ).normalize();
        // inputZ > 0 = forward, inputZ < 0 = backward
        state.velocity.copy(state.direction).multiplyScalar(MOVE_SPEED * Math.sign(inputZ));
      } else if (!moveTargetRef?.current) {
        state.velocity.set(0, 0, 0);
      }

      // Apply movement whenever we have velocity (WASD or click-to-move)
      if (state.velocity.lengthSq() > 0.001) {
        const newPos = state.position.clone().add(
          state.velocity.clone().multiplyScalar(delta),
        );
        const { position: resolved } = resolveCollisions(newPos, PLAYER_RADIUS, buildings);
        // Snap Y to terrain height
        resolved.y = terrainHeight(resolved.x, resolved.z) + 0.65;
        state.position.copy(resolved);
      } else {
        // Even when idle, stay on terrain
        state.position.y = terrainHeight(state.position.x, state.position.z) + 0.65;
      }

      // Update store
      setPlayerTransform(
        [state.position.x, state.position.y, state.position.z],
        state.facingAngle,
      );

      raf = requestAnimationFrame(tick);
    }

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [buildings, spawnPoint, setPlayerTransform]);

  return { stateRef };
}
